import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../context/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import hrBackofficeService from '../../../api/services/hrBackofficeService';
import recruitmentAnalyticsService from '../../../api/services/recruitmentAnalyticsService';
import { useCustomAlert } from '../../../hooks/useCustomAlert';
import apiClient from '../../../api/apiClient';
import { formatDate } from '../../../utils/stringUtils';
import { formatIndianNumber } from '../../../utils/amountFormatter';
import {
  User,
  Users,
  FileText,
  AlertTriangle,
  CheckCircle,
  Eye,
  Edit,
  RefreshCw,
  Activity,
  UserCheck,
  FileCheck,
  Mail,
  MessageSquare,
  Shield,
  Calendar,
  Target,
  Download,
  PlayCircle,
  XCircle,
  ChevronUp,
  ChevronDown,
  ArrowUpDown
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import DateRangeFilter from '../../../components/common/DateRangeFilter';

const HRBackofficeDashboard = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { hasPermission } = usePermissions();
  const { showError } = useCustomAlert();
  const [selectedPeriod, setSelectedPeriod] = useState('30d');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [onboardingStats, setOnboardingStats] = useState({
    totalCandidates: 0,
    pendingOnboarding: 0,
    documentsPending: 0,
    offersPending: 0,
    completedThisWeek: 0,
    totalDocumentVerification: 0,
    verificationQueue: 0,
    documentsVerified: 0,
    bgvInitiated: 0,
    bgvVerified: 0,
    bgvFailed: 0,
    communicationLogs: 0,
    approvalQueue: 0
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [sourceEffectiveness, setSourceEffectiveness] = useState([]);
  const [departmentMetrics, setDepartmentMetrics] = useState([]);
  const [quickStats, setQuickStats] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: 'department', direction: 'asc' });

  // Handle sorting for Department Metrics
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sorted department metrics
  const getSortedDepartmentMetrics = () => {
    if (!sortConfig.key) return departmentMetrics;

    return [...departmentMetrics].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      // Handle null/undefined values by type
      if (aValue === null || aValue === undefined) {
        aValue = typeof bValue === 'string' ? '' : 0;
      }
      if (bValue === null || bValue === undefined) {
        bValue = typeof aValue === 'string' ? '' : 0;
      }

      // If both are strings, compare case-insensitively
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue, undefined, { sensitivity: 'base' })
          : bValue.localeCompare(aValue, undefined, { sensitivity: 'base' });
      }

      // Numeric or mixed comparison
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  const sortedDepartmentMetrics = getSortedDepartmentMetrics();

  // Check authentication
  useEffect(() => {
    if (authLoading) {
      return; // Wait for auth to load
    }

    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    // Check if user has required permission (Dynamic RBAC)
    // Super admin or anyone with hr_backoffice read permission
    if (!hasPermission('hr_backoffice', 'read')) {
      showError('You do not have permission to access this page');
      navigate('/dashboard');
      return;
    }
  }, [isAuthenticated, authLoading, user, navigate, showError]);

  // React Query: Fetch optimized candidate counts
  const {
    data: candidateCountsData,
    isLoading: loadingCandidateCounts
  } = useQuery({
    queryKey: ['candidateCounts'],
    queryFn: async () => {
      const response = await apiClient.get('/recruitment/candidate-counts');

      if (response && response.responseCode === 200 && response.data) {
        const counts = response.data;

        return {
          totalCandidates: counts.totalCandidates,
          verifiedCandidates: counts.verifiedCandidates,
          verificationPercentage: counts.verificationPercentage,
          success: true
        };
      } else {
        throw new Error(`Invalid response from counts API: ${response?.responseMessage || 'Unknown error'}`);
      }
    },
    enabled: isAuthenticated && !authLoading,
    staleTime: 0,
    retry: 2,
    onError: (error) => {
    }
  });

  // React Query: Fetch dashboard stats
  const {
    data: statsResult,
    isLoading: loadingStats
  } = useQuery({
    queryKey: ['dashboardStats', selectedPeriod, customDateRange.startDate, customDateRange.endDate],
    queryFn: async () => {
      const options = selectedPeriod === 'custom' ? {
        startDate: customDateRange.startDate,
        endDate: customDateRange.endDate
      } : {};
      return await hrBackofficeService.getDashboardStats(selectedPeriod, options);
    },
    enabled: isAuthenticated && !authLoading,
    staleTime: 0,
    retry: 2,
    onError: (error) => {
      if (error.message.includes('Unauthorized') || error.message.includes('401')) {
        navigate('/login');
      }
    }
  });

  // React Query: Fetch recent activities
  const {
    data: activitiesResult,
    isLoading: loadingActivities
  } = useQuery({
    queryKey: ['recentActivities'],
    queryFn: async () => {
      return await hrBackofficeService.getRecentActivities(10);
    },
    enabled: isAuthenticated && !authLoading,
    staleTime: 0,
    retry: 2,
    onError: (error) => {
    }
  });

  // React Query: Fetch source effectiveness
  const {
    data: sourceResult,
    isLoading: loadingSource
  } = useQuery({
    queryKey: ['sourceEffectiveness', selectedPeriod, customDateRange.startDate, customDateRange.endDate],
    queryFn: async () => {
      const result = await recruitmentAnalyticsService.getSourceEffectiveness(
        selectedPeriod,
        'all',
        selectedPeriod === 'custom' ? customDateRange.startDate : undefined,
        selectedPeriod === 'custom' ? customDateRange.endDate : undefined
      );
      return result;
    },
    enabled: isAuthenticated && !authLoading,
    staleTime: 0,
    retry: 2,
    onError: (error) => {
    }
  });

  // React Query: Fetch department metrics
  const {
    data: departmentResult,
    isLoading: loadingDepartment
  } = useQuery({
    queryKey: ['departmentMetrics', selectedPeriod, customDateRange.startDate, customDateRange.endDate],
    queryFn: async () => {
      const result = await recruitmentAnalyticsService.getDepartmentMetrics(
        selectedPeriod,
        selectedPeriod === 'custom' ? customDateRange.startDate : undefined,
        selectedPeriod === 'custom' ? customDateRange.endDate : undefined
      );
      return result;
    },
    enabled: isAuthenticated && !authLoading,
    staleTime: 0,
    retry: 2,
    onError: (error) => {
    }
  });

  // React Query: Fetch pipeline stats
  const {
    data: pipelineResult,
    isLoading: loadingPipeline
  } = useQuery({
    queryKey: ['pipelineStats', selectedPeriod, customDateRange.startDate, customDateRange.endDate],
    queryFn: async () => {
      return await recruitmentAnalyticsService.getFunnelMetrics(
        selectedPeriod,
        'all',
        undefined,
        undefined,
        selectedPeriod === 'custom' ? customDateRange.startDate : undefined,
        selectedPeriod === 'custom' ? customDateRange.endDate : undefined
      );
    },
    enabled: isAuthenticated && !authLoading,
    staleTime: 0,
    retry: 2,
    onError: (error) => {
    }
  });

  // React Query: Fetch quick stats
  const {
    data: quickStatsResult,
    isLoading: loadingQuickStats
  } = useQuery({
    queryKey: ['quickStats'],
    queryFn: async () => {
      return await hrBackofficeService.getQuickStats();
    },
    enabled: isAuthenticated && !authLoading,
    staleTime: 0,
    retry: 2,
    onError: (error) => {
    }
  });

  // Update state when queries complete (maintain same functionality)
  useEffect(() => {
    if (statsResult && statsResult.responseCode === 200) {
      // Use stats from dashboardStats which are now period-aware
      const updatedStats = { ...statsResult.data };

      setOnboardingStats(updatedStats);
    } else if (statsResult && statsResult.responseCode !== 200) {
      // If local API fails but optimized counts API succeeded, use optimized counts data
      if (candidateCountsData?.success) {
        const fallbackStats = {
          totalCandidates: candidateCountsData.totalCandidates,
          verificationQueue: candidateCountsData.verifiedCandidates,
          pendingOnboarding: 0,
          documentsPending: 0,
          offersPending: 0,
          completedThisWeek: 0,
          communicationLogs: 0,
          approvalQueue: 0
        };
        setOnboardingStats(fallbackStats);
      }
    }
  }, [statsResult, candidateCountsData]);

  useEffect(() => {
    if (activitiesResult && activitiesResult.responseCode === 200) {
      // Filter out admin activities
      const filteredActivities = (activitiesResult.data || []).filter(activity => {
        const candidateName = (activity.candidate || '').toLowerCase();
        const user = (activity.user || '').toLowerCase();
        const position = (activity.position || '').toLowerCase();
        // Exclude activities where candidate, user, or position contains "admin"
        return !candidateName.includes('admin') &&
          !user.includes('admin') &&
          !position.includes('admin');
      });
      setRecentActivities(filteredActivities);
    } else if (activitiesResult) {
    }
  }, [activitiesResult]);

  useEffect(() => {
    if (sourceResult) {
      // Handle different response structures
      let sourceData = [];

      // Check if it's already an array (direct response)
      if (Array.isArray(sourceResult)) {
        sourceData = sourceResult;
      }
      // Check if data is in responseCode structure
      else if (sourceResult.responseCode === 200 && sourceResult.data) {
        sourceData = Array.isArray(sourceResult.data) ? sourceResult.data : [];
      }
      // Check if data property exists and is an array
      else if (sourceResult.data && Array.isArray(sourceResult.data)) {
        sourceData = sourceResult.data;
      }
      // Check for nested structure
      else if (sourceResult.data?.data && Array.isArray(sourceResult.data.data)) {
        sourceData = sourceResult.data.data;
      }

      setSourceEffectiveness(sourceData);
    }
  }, [sourceResult]);

  useEffect(() => {
    if (departmentResult) {
      // Handle different response structures
      let deptData = [];

      // Check if it's already an array (direct response)
      if (Array.isArray(departmentResult)) {
        deptData = departmentResult;
      }
      // Check if data is in responseCode structure
      else if (departmentResult.responseCode === 200 && departmentResult.data) {
        deptData = Array.isArray(departmentResult.data) ? departmentResult.data : [];
      }
      // Check if data property exists and is an array
      else if (departmentResult.data && Array.isArray(departmentResult.data)) {
        deptData = departmentResult.data;
      }
      // Check for nested structure
      else if (departmentResult.data?.data && Array.isArray(departmentResult.data.data)) {
        deptData = departmentResult.data.data;
      }

      setDepartmentMetrics(deptData);
    }
  }, [departmentResult]);

  useEffect(() => {
    if (quickStatsResult && quickStatsResult.responseCode === 200) {
      setQuickStats(quickStatsResult.data);
    } else if (quickStatsResult) {
    }
  }, [quickStatsResult]);

  // Combined loading state
  const loading = loadingCandidateCounts || loadingStats || loadingActivities || loadingSource || loadingDepartment || loadingQuickStats || loadingPipeline;

  const quickActions = [
    {
      id: 'candidate_profiles',
      title: 'Candidate Profiles',
      description: 'Manage candidate profiles and approvals',
      icon: Users,
      color: 'bg-blue-500',
      route: '/hrms/recruitment?tab=candidates'
    },
    {
      id: 'document_verification',
      title: 'Document Verification',
      description: 'Review and verify uploaded documents',
      icon: FileCheck,
      color: 'bg-green-500',
      route: '/hrms/onboarding?tab=documentVerification'
    },
    {
      id: 'offer_management',
      title: 'Offer Management',
      description: 'Publish offers and track communications',
      icon: Mail,
      color: 'bg-yellow-500',
      route: '/hrms/recruitment?tab=offerManagement'
    },
    {
      id: 'communication_logs',
      title: 'Communication Logs',
      description: 'View all candidate communications',
      icon: MessageSquare,
      color: 'bg-purple-500',
      route: '/hrms/recruitment?tab=logs'
    },

    {
      id: 'approval_queue',
      title: 'Employee List',
      description: 'Review Employee List',
      icon: Users,
      color: 'bg-pink-500',
      route: '/employee_list'
    }
  ];

  const handleQuickAction = (route) => {
    try {
      navigate(route);
    } catch (error) {
      // Fallback to dashboard if navigation fails
      navigate('/hr_backoffice_dashboard');
    }
  };

  const handleViewDetails = (type) => {
    switch (type) {
      case 'document_verification':
        navigate('/document_verification');
        break;
      case 'profile_approval':
        navigate('/candidate_profile_management');
        break;
      case 'offer_communication':
        navigate('/offer_management');
        break;
      case 'onboarding_completion':
        navigate('/orientation');
        break;
      default:
        break;
    }
  };

  const formatActivityType = (type) => {
    if (!type) return 'Activity';
    // Convert snake_case to Title Case
    return type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'document_uploaded': return <FileText className="h-4 w-4 text-blue-600" />;
      case 'offer_sent': return <Mail className="h-4 w-4 text-green-600" />;
      case 'profile_updated': return <Edit className="h-4 w-4 text-yellow-600" />;
      case 'onboarding_completed': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'interview_scheduled': return <Calendar className="h-4 w-4 text-blue-600" />;
      case 'candidate_hired': return <UserCheck className="h-4 w-4 text-green-600" />;
      case 'candidate_applied': return <Users className="h-4 w-4 text-blue-600" />;
      case 'employee_onboarded': return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'audit_log': return <Shield className="h-4 w-4 text-indigo-600" />;
      default: return <Activity className="h-4 w-4 text-gray-600" />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'document_uploaded': return 'bg-blue-50 border-blue-200';
      case 'offer_sent': return 'bg-green-50 border-green-200';
      case 'profile_updated': return 'bg-yellow-50 border-yellow-200';
      case 'onboarding_completed': return 'bg-green-50 border-green-200';
      case 'interview_scheduled': return 'bg-blue-50 border-blue-200';
      case 'candidate_hired': return 'bg-green-50 border-green-200';
      case 'candidate_applied': return 'bg-blue-50 border-blue-200';
      case 'employee_onboarded': return 'bg-green-50 border-green-200';
      case 'audit_log': return 'bg-indigo-50 border-indigo-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'low': return 'text-green-600 bg-green-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  // Export to CSV function
  const handleExportToCSV = () => {
    try {
      const csvRows = [];
      const timestamp = new Date().toISOString().split('T')[0];

      // Add KPI Stats Section
      csvRows.push('=== KPI STATISTICS ===');
      csvRows.push('Metric,Value');
      csvRows.push(`Total Candidates,${onboardingStats.totalCandidates || 0}`);
      csvRows.push(`Total Employees,${quickStats.totalEmployees || 0}`);
      csvRows.push(`Document Verification,${onboardingStats.verificationQueue || 0}`);
      csvRows.push('');

      // Add Source Effectiveness Section
      csvRows.push('=== SOURCE EFFECTIVENESS ===');
      csvRows.push('Source,Applications,Hires,Conversion (%)');
      if (sourceEffectiveness.length > 0) {
        sourceEffectiveness.forEach((source) => {
          const sourceName = source.source === 'linkedin' ? 'LinkedIn' :
            source.source === 'referral' ? 'Referral' :
              source.source === 'job_boards' ? 'Job Boards' :
                source.source === 'company_website' ? 'Company Website' :
                  source.source === 'recruitment_agencies' ? 'Recruitment Agencies' :
                    source.source === 'career_page' ? 'Career Page' :
                      source.source || 'Unknown';
          csvRows.push(`${sourceName},${source.applications || 0},${source.hires || 0},${source.conversion || 0}`);
        });
      } else {
        csvRows.push('No data available');
      }
      csvRows.push('');

      // Add Recent Activities Section
      csvRows.push('=== RECENT ACTIVITIES ===');
      csvRows.push('Candidate,Activity Type,Timestamp');
      if (recentActivities.length > 0) {
        recentActivities.forEach((activity) => {
          const activityType = formatActivityType(activity.type || 'Unknown');
          const timestamp = activity.timestamp
            ? formatDate(activity.timestamp, { includeTime: true })
            : 'N/A';
          csvRows.push(`"${activity.candidate || 'N/A'}",${activityType},"${timestamp}"`);
        });
      } else {
        csvRows.push('No activities found');
      }
      csvRows.push('');

      // Add Department Metrics Section
      csvRows.push('=== DEPARTMENT METRICS ===');
      csvRows.push('Department,Applications,Interviews,Offers,Hires,Avg Time to Hire (days)');
      if (departmentMetrics.length > 0) {
        departmentMetrics.forEach((dept) => {
          const departmentName = dept.department || 'Unknown Department';
          csvRows.push(`"${departmentName}",${dept.applications || 0},${dept.interviews || 0},${dept.offers || 0},${dept.hires || 0},${dept.avgTimeToHire || 0}`);
        });
      } else {
        csvRows.push('No department metrics found');
      }

      // Create CSV content
      const csvContent = csvRows.join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `HR_Backoffice_Dashboard_${timestamp}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      showError('Failed to export data. Please try again.');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">
            {authLoading ? 'Checking authentication...' : 'Loading dashboard data...'}
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // Will redirect to login
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">HR Overview</h1>
          <p className="text-gray-600">Manage candidate onboarding tasks and approvals</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="flex items-center space-x-2">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
              <option value="custom">Custom Date</option>
            </select>
          </div>
          {selectedPeriod === 'custom' && (
            <div className="flex items-center">
              <DateRangeFilter
                startDate={customDateRange.startDate}
                endDate={customDateRange.endDate}
                onChange={(range) => setCustomDateRange(range)}
              />
            </div>
          )}
          <Button
            variant="outline"
            icon={<Download className="h-4 w-4" />}
            onClick={handleExportToCSV}
            disabled={loading}
          >
            Export
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* 1. Total Applicants */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Applicants</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(pipelineResult?.totals?.applied || onboardingStats.totalCandidates)}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* 2. Shortlisted */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Shortlisted</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(pipelineResult?.statusBreakdown?.shortlisted)}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-full">
              <Users className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>

        {/* 3. Interviewed */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Interviewed</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(pipelineResult?.statusBreakdown?.interviewed)}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        {/* 4. Offered */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Offered</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatIndianNumber((pipelineResult?.statusBreakdown?.offer || 0) + (pipelineResult?.statusBreakdown?.offered || 0))}
              </p>
            </div>
            <div className="p-3 bg-indigo-100 rounded-full">
              <FileCheck className="h-6 w-6 text-indigo-600" />
            </div>
          </div>
        </div>

        {/* 5. Hired */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Hired</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(pipelineResult?.statusBreakdown?.hired)}</p>
            </div>
            <div className="p-3 bg-emerald-100 rounded-full">
              <UserCheck className="h-6 w-6 text-emerald-600" />
            </div>
          </div>
        </div>

        {/* 6. Rejected */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Rejected</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(pipelineResult?.statusBreakdown?.rejected)}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <AlertTriangle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>

        {/* 7. Total Document Verification */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Document Verification</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(onboardingStats.totalDocumentVerification)}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <FileCheck className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* 8. Document Verification (Pending) */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Document Verification Pending</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(onboardingStats.verificationQueue)}</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-full">
              <RefreshCw className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>

        {/* 9. Document Verified */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Document Verified</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(onboardingStats.documentsVerified)}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* 10. Background Verification Initiated */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">BG Verification Initiated</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(onboardingStats.bgvInitiated)}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <PlayCircle className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* 11. Background Verification Verified */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">BG Verification Verified</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(onboardingStats.bgvVerified)}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* 12. Background Verification Failed */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">BG Verification Failed</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(onboardingStats.bgvFailed)}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <XCircle className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>

        {/* 13. Total Staff */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Staff</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(quickStats.totalEmployees)}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <UserCheck className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        {/* 14. Active Staff */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Staff</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(quickStats.activeEmployees)}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <Activity className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* 15. Inactive Staff */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Inactive Staff</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(quickStats.inactiveEmployees)}</p>
            </div>
            <div className="p-3 bg-gray-100 rounded-full">
              <Shield className="h-6 w-6 text-gray-600" />
            </div>
          </div>
        </div>

        {/* 16. Exit Staff */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Exit Staff</p>
              <p className="text-2xl font-bold text-gray-900">{formatIndianNumber(quickStats.exitStaff)}</p>
            </div>
            <div className="p-3 bg-red-100 rounded-full">
              <Target className="h-6 w-6 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {quickActions.map(action => (
            <button
              key={action.id}
              onClick={(e) => {
                e.preventDefault();
                handleQuickAction(action.route);
              }}
              className="p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:shadow-sm transition-all duration-200 text-left cursor-pointer"
            >
              <div className="flex items-center space-x-3">
                <div className={`p-2 rounded-lg ${action.color}`}>
                  <action.icon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">{action.title}</h3>
                  <p className="text-sm text-gray-600">{action.description}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {/* Source Effectiveness */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Source Effectiveness</h2>
            <Target className="h-5 w-5 text-gray-400" />
          </div>
          {sourceEffectiveness.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Target className="h-8 w-8 mx-auto mb-2 text-gray-400" />
              <p>No source effectiveness data found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sourceEffectiveness.map((source, index) => {
                const sourceName = source.source === 'linkedin' ? 'LinkedIn' :
                  source.source === 'referral' ? 'Referral' :
                    source.source === 'job_boards' ? 'Job Boards' :
                      source.source === 'company_website' ? 'Company Website' :
                        source.source === 'recruitment_agencies' ? 'Recruitment Agencies' :
                          source.source === 'career_page' ? 'Career Page' :
                            source.source;

                return (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-gray-900 capitalize">{sourceName}</p>
                      <p className="text-xs text-gray-600">{formatIndianNumber(source.applications)} applications, {formatIndianNumber(source.hires)} hires</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">{source.conversion || 0}%</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Activities */}
        <div className="bg-white p-6 rounded-lg border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Activities</h2>
          </div>
          <div className="space-y-3">
            {recentActivities.length > 0 ? (
              recentActivities.map(activity => (
                <div key={activity.id} className={`p-4 rounded-xl border-l-4 shadow-sm transition-all hover:shadow-md ${getActivityColor(activity.type)}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start space-x-3 flex-1 min-w-0">
                      <div className={`flex-shrink-0 p-2 rounded-lg bg-white shadow-sm`}>
                        {getActivityIcon(activity.type)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
                            {activity.type === 'audit_log' ? activity.position : formatActivityType(activity.type)}
                          </span>
                          <span className="text-[10px] text-gray-400">•</span>
                          <span className="text-xs text-gray-500 font-medium">
                            {activity.timestamp ? formatDate(activity.timestamp, { includeTime: true }) : 'N/A'}
                          </span>
                        </div>
                        <p className="text-sm font-semibold text-gray-900 leading-snug">
                          {activity.description || `${activity.candidate} - ${formatActivityType(activity.type)}`}
                        </p>
                        {activity.user && (
                          <p className="text-[11px] text-gray-500 mt-1 flex items-center">
                            <User className="h-3 w-3 mr-1" />
                            By {activity.user}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-300">
                <Activity className="h-10 w-10 mx-auto mb-3 text-gray-300" />
                <p className="text-gray-500 font-medium">No recent activities found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Department Metrics */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Department Metrics</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group transition-colors"
                  onClick={() => handleSort('department')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Department</span>
                    {sortConfig.key === 'department' ? (
                      sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-300 group-hover:text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group transition-colors"
                  onClick={() => handleSort('applications')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Applications</span>
                    {sortConfig.key === 'applications' ? (
                      sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-300 group-hover:text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group transition-colors"
                  onClick={() => handleSort('interviews')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Interviews</span>
                    {sortConfig.key === 'interviews' ? (
                      sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-300 group-hover:text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group transition-colors"
                  onClick={() => handleSort('offers')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Offers</span>
                    {sortConfig.key === 'offers' ? (
                      sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-300 group-hover:text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group transition-colors"
                  onClick={() => handleSort('hires')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Hires</span>
                    {sortConfig.key === 'hires' ? (
                      sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-300 group-hover:text-gray-400" />
                    )}
                  </div>
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 group transition-colors"
                  onClick={() => handleSort('avgTimeToHire')}
                >
                  <div className="flex items-center space-x-1">
                    <span>Avg. Time to Hire</span>
                    {sortConfig.key === 'avgTimeToHire' ? (
                      sortConfig.direction === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ArrowUpDown className="h-4 w-4 text-gray-300 group-hover:text-gray-400" />
                    )}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {departmentMetrics.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <Users className="h-12 w-12 text-gray-400 mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        No department metrics found
                      </h3>
                      <p className="text-gray-500">
                        No department data available for the selected period.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                sortedDepartmentMetrics.map((dept, index) => {
                  const departmentName = dept.department || 'Unknown Department';
                  return (
                    <tr key={index} className="hover:bg-gray-50">
                      <td
                        className="px-6 py-4 text-sm font-medium text-gray-900 max-w-[240px] truncate capitalize"
                        title={departmentName}
                      >
                        {departmentName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatIndianNumber(dept.applications)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatIndianNumber(dept.interviews)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatIndianNumber(dept.offers)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatIndianNumber(dept.hires)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{dept.avgTimeToHire || 0} days</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

    </div >
  );
};

export default HRBackofficeDashboard;
